import { config } from "./config";
import { renewAccessToken } from "./auth";
import type {
  AuditEvent,
  AssignableAdministrator,
  CreateEnrollmentRequest,
  DeviceDetail,
  DeviceAssignmentResult,
  DeviceSummary,
  EmailRevocationResult,
  EnrollmentCancellationResult,
  EnrollmentCreated,
  EnrollmentSummary,
  FleetReport,
  MonitoringEventsResponse,
  RegistrationSummary,
  RecipientRevocationResult,
  RevocationResult,
} from "./types";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly correlationId?: string,
  ) {
    super(message);
  }
}

async function fetchJson(path: string, accessToken: string | null, init: RequestInit): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (init.body) headers.set("content-type", "application/json");
  if (accessToken) headers.set("authorization", `Bearer ${accessToken}`);
  return fetch(`${config.apiBaseUrl}${path}`, { ...init, headers, cache: "no-store" });
}

async function request<T>(path: string, accessToken: string | null, init: RequestInit = {}): Promise<T> {
  let response = await fetchJson(path, accessToken, init);
  if (response.status === 401 && accessToken) {
    try {
      const renewed = await renewAccessToken();
      if (renewed?.access_token && !renewed.expired) {
        response = await fetchJson(path, renewed.access_token, init);
      }
    } catch {
      throw new ApiError("Your session expired. Sign in again to continue.", 401);
    }
  }
  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    throw new ApiError(String(payload.message ?? "Request failed"), response.status, String(payload.correlation_id ?? ""));
  }
  return payload as T;
}

async function download(path: string, accessToken: string): Promise<void> {
  const perform = (token: string) => fetch(`${config.apiBaseUrl}${path}`, {
    headers: { accept: "text/csv", authorization: `Bearer ${token}` }, cache: "no-store",
  });
  let response = await perform(accessToken);
  if (response.status === 401) {
    try {
      const renewed = await renewAccessToken();
      if (renewed?.access_token && !renewed.expired) response = await perform(renewed.access_token);
    } catch {
      throw new ApiError("Your session expired. Sign in again to continue.", 401);
    }
  }
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    throw new ApiError(String(payload.message ?? "Report export failed"), response.status);
  }
  const blobUrl = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = response.headers.get("content-disposition")?.match(/filename="([^"]+)"/)?.[1] ?? "bottimo-report.csv";
  anchor.click();
  URL.revokeObjectURL(blobUrl);
}

export const api = {
  fleet: (token: string) => request<FleetReport>("/admin/v2/reports/fleet", token),
  devices: (token: string) => request<{ items: DeviceSummary[] }>("/admin/v2/devices", token),
  administrators: (token: string) =>
    request<{ items: AssignableAdministrator[] }>("/admin/v2/administrators", token),
  device: (token: string, deviceId: string) => request<DeviceDetail>(`/admin/v2/devices/${encodeURIComponent(deviceId)}`, token),
  assignDeviceAdmin: (token: string, deviceId: string, assigneeSubject: string, reason: string) =>
    request<DeviceAssignmentResult>(`/admin/v2/devices/${encodeURIComponent(deviceId)}/assignments`, token, {
      method: "POST",
      body: JSON.stringify({ assignee_subject: assigneeSubject, reason }),
    }),
  registrations: (token: string, deviceId: string) =>
    request<{ items: RegistrationSummary[] }>(`/admin/v2/devices/${encodeURIComponent(deviceId)}/registrations`, token),
  revoke: (token: string, deviceId: string, reason: string) =>
    request<RevocationResult>(`/admin/v2/devices/${encodeURIComponent(deviceId)}/revocations`, token, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  revokeEmail: (token: string, deviceId: string, reason: string) =>
    request<EmailRevocationResult>(`/admin/v2/devices/${encodeURIComponent(deviceId)}/email-revocations`, token, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  revokeRegistration: (token: string, deviceId: string, registrationId: string, reason: string) =>
    request<RecipientRevocationResult>(
      `/admin/v2/devices/${encodeURIComponent(deviceId)}/registrations/${encodeURIComponent(registrationId)}/revocations`,
      token,
      { method: "POST", body: JSON.stringify({ reason }) },
    ),
  enrollments: (token: string) => request<{ items: EnrollmentSummary[] }>("/admin/v2/enrollments", token),
  audit: (token: string) => request<{ items: AuditEvent[] }>("/admin/v2/audit", token),
  createEnrollment: (token: string, enrollment: CreateEnrollmentRequest) =>
    request<EnrollmentCreated>("/admin/v2/enrollments", token, {
      method: "POST",
      body: JSON.stringify(enrollment),
    }),
  cancelEnrollment: (token: string, enrollmentId: string, reason: string) =>
    request<EnrollmentCancellationResult>(
      `/admin/v2/enrollments/${encodeURIComponent(enrollmentId)}/cancellations`,
      token,
      { method: "POST", body: JSON.stringify({ reason }) },
    ),
  monitoring: (token: string, limit = 100) =>
    request<MonitoringEventsResponse>(`/admin/v2/monitoring/events?limit=${limit}`, token),
  inspectConfirmation: (proof: string) =>
    request<{ state: string; masked_email: string; expires_at: string }>("/public/v1/email-confirmations/inspect", null, {
      method: "POST",
      body: JSON.stringify({ token: proof }),
    }),
  confirmEmail: (proof: string) =>
    request<{ state: "VERIFIED" }>("/public/v1/email-confirmations/confirm", null, {
      method: "POST",
      body: JSON.stringify({ token: proof }),
    }),
  exportReport: (token: string, report: string) => download(`/admin/v2/reports/export.csv?report=${encodeURIComponent(report)}`, token),
};
